/**
 * presets.ts — Quick presets pra pagina /notificacoes/preferencias.
 *
 * Spec: roadmap notificacoes §1 ultrathink (linha 880) — 4 quick presets
 * + smart defaults via persona mapping (applied_template_id).
 *
 * Cada preset gera um array de `SubscriptionUpdate` aplicado em batch via
 * PATCH /v1/me/notification-subscriptions (uma chamada para todos).
 *
 * 3 presets concretos + 1 sentinela 'custom' (no-op, apenas marca que o user
 * personalizou manualmente — quando selecionado mostra "Ja personalizado").
 */
import type {
  SubscriptionChannel,
  SubscriptionSeverity,
  SubscriptionUpdate,
} from '@/api/notification-preferences'
import { PREFERENCE_SOURCES } from './preferencias-constants'

export type PresetName = 'devops_pro' | 'gerente' | 'conservador' | 'custom'

export interface PresetItem {
  source: string
  channel: SubscriptionChannel
  enabled: boolean
  min_severity: SubscriptionSeverity
}

/**
 * Helper: gera SubscriptionUpdate compativel com PATCH a partir de PresetItem.
 *
 * `event_type: null` significa "default global pra source" — backend trata como
 * default subscription (nao um event-type especifico).
 */
export function presetItemsToUpdates(items: PresetItem[]): SubscriptionUpdate[] {
  return items.map((it) => ({
    source: it.source,
    event_type: null,
    channel: it.channel,
    enabled: it.enabled,
    min_severity: it.min_severity,
  }))
}

// Sources cobertas pelos presets: DERIVADAS de PREFERENCE_SOURCES (a lista que a
// tela usa), sem copia manual. Fonte nova na tela entra aqui sozinha, e o teste
// `__tests__/presets.test.ts` reprova se algum preset nao a decidir. (TASK-12194)
const ALL_SOURCES: readonly string[] = PREFERENCE_SOURCES

// ---------------------------------------------------------------------------
// Preset 1: DevOps Pro
// ---------------------------------------------------------------------------
//
// Perfil: engenheiro de plantao que precisa de visibilidade ampla + WhatsApp
// pra incidentes criticos de infra.
//
// - cloudwatch_alarm/guardduty/ztna/zabbix + cloudtrail/alb_target_health/
//   ecs_service/security_auth/wazuh → feed+email+webpush, min=info
// - gmud_lifecycle / provisioning / provisioning_saga / host_order → feed+email, min=info
// - terraform_runner → feed+webpush, min=info (webpush e opt-in; consoleai#26384)
// - gitlab / cicd_gitlab / cost_anomaly → feed, min=warning (cost_anomaly segue o finops)
// - audit / notification / inbox / chamado → feed, min=info
// - aws_health / aws_backup / securityhub → feed, min=warning
// - database / rbac / finops / email → feed, min=warning
// - WhatsApp: somente cloudwatch_alarm em critical+
const DEVOPS_PRO_FULL_FEED_INFRA = [
  'cloudwatch_alarm', 'guardduty', 'ztna', 'zabbix',
  'cloudtrail', 'alb_target_health', 'ecs_service', 'security_auth', 'wazuh',
] as const
const DEVOPS_PRO_FEED_EMAIL_INFRA = [
  'gmud_lifecycle', 'provisioning', 'provisioning_saga', 'host_order',
] as const
const DEVOPS_PRO_FEED_WEBPUSH = ['terraform_runner'] as const
const DEVOPS_PRO_FEED_ONLY_INFO = ['audit', 'notification', 'inbox', 'chamado'] as const
const DEVOPS_PRO_FEED_ONLY_WARNING = [
  // 'email' removido — engano (email eh channel)
  'gitlab', 'cicd_gitlab', 'aws_health', 'aws_backup', 'securityhub',
  'database', 'rbac', 'finops', 'cost_anomaly',
] as const

const devopsPro: PresetItem[] = [
  // Full feed+email+webpush em info pra fontes criticas de infra
  ...DEVOPS_PRO_FULL_FEED_INFRA.flatMap<PresetItem>((src) => [
    { source: src, channel: 'feed', enabled: true, min_severity: 'info' },
    { source: src, channel: 'email', enabled: true, min_severity: 'info' },
    { source: src, channel: 'webpush', enabled: true, min_severity: 'info' },
  ]),
  // feed+email em info pra GMUD e provisioning (sagas longas, quer email)
  ...DEVOPS_PRO_FEED_EMAIL_INFRA.flatMap<PresetItem>((src) => [
    { source: src, channel: 'feed', enabled: true, min_severity: 'info' },
    { source: src, channel: 'email', enabled: true, min_severity: 'info' },
  ]),
  // feed+webpush em info pro runner de Terraform (plan/apply pedem acao rapida)
  ...DEVOPS_PRO_FEED_WEBPUSH.flatMap<PresetItem>((src) => [
    { source: src, channel: 'feed', enabled: true, min_severity: 'info' },
    { source: src, channel: 'webpush', enabled: true, min_severity: 'info' },
  ]),
  // feed-only info pra ruido baixo (audit/inbox/chamado/notification)
  ...DEVOPS_PRO_FEED_ONLY_INFO.map<PresetItem>((src) => ({
    source: src, channel: 'feed', enabled: true, min_severity: 'info',
  })),
  // feed-only warning pra fontes secundarias (gitlab/aws_health/etc)
  ...DEVOPS_PRO_FEED_ONLY_WARNING.map<PresetItem>((src) => ({
    source: src, channel: 'feed', enabled: true, min_severity: 'warning',
  })),
  // WhatsApp: somente cloudwatch_alarm em critical+
  { source: 'cloudwatch_alarm', channel: 'whatsapp', enabled: true, min_severity: 'critical' },
]

// ---------------------------------------------------------------------------
// Preset 2: Gerente
// ---------------------------------------------------------------------------
//
// Perfil: gerente/coordenador que quer overview operacional sem ruido tecnico.
//
// - chamado / inbox / gmud_lifecycle → feed+email, min=warning
// - cloudwatch_alarm / audit → feed (sem email), min=critical
// - outros (inclui cost_anomaly e as fontes novas de infra/seguranca) → feed, min=critical
// - WhatsApp: NONE
const GERENTE_FEED_EMAIL = ['chamado', 'inbox', 'gmud_lifecycle'] as const
const GERENTE_FEED_CRITICAL = ['cloudwatch_alarm', 'audit'] as const
// Outros = ALL_SOURCES - (FEED_EMAIL ∪ FEED_CRITICAL)
const GERENTE_OUTROS = ALL_SOURCES.filter(
  (s) => !GERENTE_FEED_EMAIL.includes(s as never) && !GERENTE_FEED_CRITICAL.includes(s as never),
)

const gerente: PresetItem[] = [
  ...GERENTE_FEED_EMAIL.flatMap<PresetItem>((src) => [
    { source: src, channel: 'feed', enabled: true, min_severity: 'warning' },
    { source: src, channel: 'email', enabled: true, min_severity: 'warning' },
  ]),
  ...GERENTE_FEED_CRITICAL.map<PresetItem>((src) => ({
    source: src, channel: 'feed', enabled: true, min_severity: 'critical',
  })),
  ...GERENTE_OUTROS.map<PresetItem>((src) => ({
    source: src, channel: 'feed', enabled: true, min_severity: 'critical',
  })),
]

// ---------------------------------------------------------------------------
// Preset 3: Conservador
// ---------------------------------------------------------------------------
//
// Perfil: user que quer ruido minimo — somente alertas criticos no feed,
// nenhum email/push/whatsapp.
//
// - ALL → feed only, min=critical
// - Demais canais (email/webpush/whatsapp): NONE (enabled=false)
const conservador: PresetItem[] = [
  ...ALL_SOURCES.map<PresetItem>((src) => ({
    source: src, channel: 'feed', enabled: true, min_severity: 'critical',
  })),
  // Garantir que email/webpush/whatsapp estao desligados em TODAS as sources
  ...ALL_SOURCES.flatMap<PresetItem>((src) => [
    { source: src, channel: 'email', enabled: false, min_severity: 'critical' },
    { source: src, channel: 'webpush', enabled: false, min_severity: 'critical' },
    { source: src, channel: 'whatsapp', enabled: false, min_severity: 'critical' },
  ]),
]

export const PRESETS: Record<Exclude<PresetName, 'custom'>, PresetItem[]> = {
  devops_pro: devopsPro,
  gerente,
  conservador,
}

/**
 * Persona-aware mapping pra smart-default callout no primeiro acesso.
 *
 * Chave = `applied_template_name` do user (RBAC v4 Fase 1, TASK-2548).
 * Valor = preset recomendado.
 *
 * Templates nao listados → sem callout (user nao tem persona mapeada).
 */
export const TEMPLATE_TO_PRESET: Record<string, PresetName> = {
  // DevOps / engenharia → tudo + WhatsApp critico
  'Administrador': 'devops_pro',
  'DevOps': 'devops_pro',
  'SRE': 'devops_pro',
  'Engenheiro': 'devops_pro',
  // Gestao → overview operacional
  'Gestor': 'gerente',
  'Gerente': 'gerente',
  'Coordenador': 'gerente',
  'Product Owner': 'gerente',
  // Compliance / auditoria → ruido minimo
  'Auditor': 'conservador',
  'Compliance': 'conservador',
  'Read-Only': 'conservador',
  'Viewer': 'conservador',
}

export const PRESET_LABELS: Record<PresetName, {
  name: string
  description: string
  icon: 'IconBolt' | 'IconUserCog' | 'IconShieldLock' | 'IconAdjustments'
  color: string
}> = {
  devops_pro: {
    name: 'DevOps Pro',
    description: 'Tudo + alertas críticos via WhatsApp',
    icon: 'IconBolt',
    color: 'orange',
  },
  gerente: {
    name: 'Gerente',
    description: 'Resumo operacional, sem ruído técnico',
    icon: 'IconUserCog',
    color: 'blue',
  },
  conservador: {
    name: 'Conservador',
    description: 'Apenas alertas críticos no feed',
    icon: 'IconShieldLock',
    color: 'gray',
  },
  custom: {
    name: 'Custom',
    description: 'Configurado manualmente nos cards abaixo',
    icon: 'IconAdjustments',
    color: 'grape',
  },
}

export const PRESET_NAMES: PresetName[] = ['devops_pro', 'gerente', 'conservador', 'custom']

export const CALLOUT_DISMISSED_KEY = 'notif_presets_callout_dismissed'
